import React, { useState, useEffect, useMemo } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { getSquadPillarScores, getPhysicalBreakdown } from '../api';
import { getPercentileColor } from './physicalTestingShared';
import StarRating from '../components/StarRating';
import PageChrome from '../components/PageChrome';

// Small "vs team" percentile badge — same 0-100 color scale as ISRT %/Sprint % on the Physical
// Testing page (2026-08-04): 100% = best in the team, 75% means 25% of the team scored better.
// Pill-shaped and visually distinct from the tile's own score so the two percentages (a raw
// formula score, which can exceed 100%, vs. this squad ranking) don't read as the same number.
function TeamPercentileBadge({ percentile }) {
  if (percentile == null) return null;
  const color = getPercentileColor(percentile);
  return (
    <span
      title={percentile === 100 ? 'Best in the team' : `${100 - percentile}% of the team scored better`}
      style={{
        fontSize: 10, fontWeight: 700, color, background: `${color}22`,
        borderRadius: 99, padding: '1px 6px', whiteSpace: 'nowrap',
      }}>
      {percentile}% vs team
    </span>
  );
}

const PERIODS = [
  { value: 'week', label: 'Week' },
  { value: '28day', label: '28-Day' },
  { value: 'preseason', label: 'Pre-season' },
  { value: 'season', label: 'Season' },
];

const COLUMN_COUNT = 10; // expand-chevron + Player + Position + Stars + Overall + 5 pillars

// Same visual pattern as Availability.js/MaxSpeed.js's SortHeader (not a shared component in
// this codebase — each sortable table defines its own copy). NULL handling below is stricter
// than those: NULLs sort to the bottom regardless of direction, not just via a `?? -1` that
// only works for descending.
function SortHeader({ label, sortKey, activeKey, dir, onClick, align }) {
  const active = sortKey === activeKey;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th onClick={() => onClick(sortKey)} style={{ textAlign: align || 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: active ? 'var(--text-primary)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>{label} <Icon size={11} /></span>
    </th>
  );
}

// NULLs always sort last, in both ascending and descending order — a "no data" player
// shouldn't rank as if they scored 0.
function compareRows(a, b, key, dir) {
  const av = a[key], bv = b[key];
  const aNull = av === null || av === undefined;
  const bNull = bv === null || bv === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  return dir === 'asc' ? av - bv : bv - av;
}

const numCell = { padding: '10px 14px', textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' };

function PillarCell({ value, highlyImputed }) {
  if (value == null) return <td style={{ ...numCell, color: 'var(--text-dimmed)' }}>no data</td>;
  return (
    <td style={numCell}>
      {Math.round(value)}
      {highlyImputed && (
        <span title="More than 30% of the chronic window behind this score is estimated, not measured GPS data" style={{ marginLeft: 4, fontSize: 10, color: '#b45309' }}>⚠</span>
      )}
    </td>
  );
}

// One labeled sub-score in the Physical breakdown panel — a stat-tile style block, not a
// second table, since there are only three of these.
function SubScoreTile({ label, score, detail, percentile }) {
  return (
    <div style={{ background: 'var(--bg-app)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', minWidth: 150 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: score == null ? 'var(--text-dimmed)' : 'var(--text-primary)' }}>
          {score == null ? 'no data' : Math.round(score)}
        </div>
        <TeamPercentileBadge percentile={percentile} />
      </div>
      {detail && <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 2 }}>{detail}</div>}
    </div>
  );
}

// A smaller nested tile for the training/match side of a SubScoreTile — same visual language,
// one level down (2026-07-30, Physical formula transparency).
function SideTile({ label, score, detail, emptyLabel, percentile }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', minWidth: 150, flex: 1 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-dimmed)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: score == null ? 'var(--text-dimmed)' : 'var(--text-primary)' }}>
          {score == null ? (emptyLabel || 'no data') : `${Math.round(score)}%`}
        </div>
        {score != null && <TeamPercentileBadge percentile={percentile} />}
      </div>
      {detail && <div style={{ fontSize: 10.5, color: 'var(--text-dimmed)', marginTop: 2 }}>{detail}</div>}
    </div>
  );
}

// The 45% score is the AVERAGE of the two sub-tiles below it:
//   A "Full load"         — training + actual match minutes + Match-RUNS, vs a ceiling that is
//                           always 4 training days + 1 match (it does NOT shrink for players who
//                           didn't feature — making the load up is the point).
//   B "Match performance" — the same match recalculated to 90 minutes, vs the n-match ceiling.
// With no qualifying match, B doesn't exist and the score is A alone.
const fmtM = (v) => (v != null ? `${Math.round(v).toLocaleString()}m` : '?m');

function CumulativeSubScoreTile({ label, overallScore, qualifyingMatchCount, loadScore, loadRaw, loadCeiling, loadPercentile, trainingRaw, matchRaw, runsRaw, matchScore, matchPerfRaw, matchCeiling, matchPercentile, distinctDays, ceilingMatchCount }) {
  const hasMatch = qualifyingMatchCount > 0;
  const parts = [`training ${fmtM(trainingRaw)}`];
  if (matchRaw) parts.push(`match ${fmtM(matchRaw)}`);
  if (runsRaw)  parts.push(`runs ${fmtM(runsRaw)}`);
  // Ceiling composition — the match allowance is 1 for the week however many were played, but
  // scales with the number of matches the TEAM played over 28-day/pre-season/season.
  const ceilingNote = distinctDays != null && ceilingMatchCount != null
    ? `ceiling ${distinctDays} training day${distinctDays === 1 ? '' : 's'} + ${ceilingMatchCount} match${ceilingMatchCount === 1 ? '' : 'es'}`
    : null;
  return (
    <div style={{ background: 'var(--bg-app)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', minWidth: 340, flex: '1 1 340px' }}>
      {/* Score is kept hard-left, not floated right: the breakdown row lives inside a
          horizontally-scrolling table, so a right-aligned score is clipped off-screen on a
          phone and a sub-tile's % becomes the only visible number. */}
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 2 }}>
        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, color: overallScore == null ? 'var(--text-dimmed)' : 'var(--text-primary)' }}>
          {overallScore == null ? 'no data' : Math.round(overallScore)}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          {hasMatch && loadScore != null && matchScore != null
            ? `= (${Math.round(loadScore)} + ${Math.round(matchScore)}) / 2 — average of the two below`
            : 'full load only — no qualifying match ≥40 min'}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        <SideTile
          label="A · Full load"
          score={loadScore}
          percentile={loadPercentile}
          detail={loadCeiling != null
            ? `${fmtM(loadRaw)} / ${fmtM(loadCeiling)} — ${parts.join(' + ')}${ceilingNote ? ` · ${ceilingNote}` : ''}`
            : null}
        />
        <SideTile
          label="B · Match performance (110%)"
          score={hasMatch ? matchScore : null}
          percentile={matchPercentile}
          emptyLabel="no match"
          detail={hasMatch
            ? `${fmtM(matchPerfRaw)} / ${fmtM(matchCeiling)}, recalculated to 90 min`
            : 'not scored — score above is A alone'}
        />
      </div>
    </div>
  );
}

// Physical = 0.10×EWMA + 0.45×Cumulative-Distance + 0.45×Cumulative-HMLD (reweighted
// 2026-07-27, previously 0.35/0.325/0.325, originally 0.5/0.25/0.25 at Stage 3) — this is
// the "why this percentage" expansion (2026-07-23), fetched fresh per row on first expand
// (not persisted in pillar_scores_period — see the /physical-breakdown endpoint's comment).
function PhysicalBreakdownRow({ playerId, period }) {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getPhysicalBreakdown(playerId, period)
      .then(setBreakdown)
      .catch(() => setBreakdown(null))
      .finally(() => setLoading(false));
  }, [playerId, period]);

  return (
    <tr>
      <td colSpan={COLUMN_COUNT} style={{ padding: '14px 14px 18px 44px', background: 'var(--bg-app)', borderTop: '1px solid var(--border)' }}>
        {loading ? (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading breakdown…</span>
        ) : !breakdown || breakdown.physical == null ? (
          <span style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>
            No GPS-tracked sessions in this window{breakdown?.date_from && breakdown?.date_to ? ` (${breakdown.date_from} → ${breakdown.date_to})` : ''} — nothing to break down. The score shown in the table is from a previous computation and is stale.
          </span>
        ) : (
          <div>
            {breakdown.date_from && breakdown.date_to && (
              <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 6 }}>
                Window: {breakdown.date_from} → {breakdown.date_to}
                {breakdown.cumulativeTD != null && (
                  <span style={{ marginLeft: 10 }}>
                    · Total TD: <strong>{Math.round(breakdown.cumulativeTD).toLocaleString()} m</strong>
                    {(() => {
                      const bits = [];
                      if (breakdown.trainingTD   != null) bits.push(`training ${Math.round(breakdown.trainingTD).toLocaleString()} m`);
                      if (breakdown.actualMatchTD)        bits.push(`match ${Math.round(breakdown.actualMatchTD).toLocaleString()} m actual`);
                      if (breakdown.matchRunsTD)          bits.push(`runs ${Math.round(breakdown.matchRunsTD).toLocaleString()} m`);
                      return bits.length ? ` (${bits.join(' + ')})` : '';
                    })()}
                  </span>
                )}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
              Physical = 0.10 × EWMA + 0.45 × Cumulative Distance + 0.45 × Cumulative HMLD
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 10 }}>
              Cumulative Distance/HMLD each = average of <strong>A</strong> and <strong>B</strong>. A (full load) = (training + match at actual minutes + post-match runs) / (training days + match allowance) — the same ceiling for everyone, including players who didn't feature, so a shortfall shows instead of the bar dropping. B (match performance) = the same match recalculated to 90 min / match target. No qualifying match ≥40 min → the score is A alone. A's match allowance is fixed at 1 for the week (so a 2-match week scores above 100%) and scales with the number of matches the <em>team</em> played over 28-day / pre-season / season.
            </div>
            {breakdown.chronicHighlyImputed && (
              <div style={{ background: '#fff3cd', border: '1px solid #ffe08a', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 11 }}>
                {Math.round((breakdown.chronicImputedPct || 0) * 100)}% of the chronic window behind this EWMA ratio is estimated, not measured — treat as a rough guide.
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <SubScoreTile
                label="EWMA (ACWR ratio) — 10%"
                score={breakdown.ewmaScore}
                detail={breakdown.ratio != null ? `ratio ${breakdown.ratio.toFixed(2)} (acute ${Math.round(breakdown.acute)} / chronic ${Math.round(breakdown.chronic)})` : null}
              />
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--text-dimmed)', marginBottom: 8 }}>
              "vs team" badges show how each score compares to the rest of the squad this period — 100% means the best in the team, 75% means 25% of the team scored better. Same scale as ISRT %/Sprint % on Physical Testing.
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <CumulativeSubScoreTile
                label="Cumulative Distance — 45%"
                overallScore={breakdown.distanceScore}
                qualifyingMatchCount={breakdown.qualifyingMatchCount}
                loadScore={breakdown.scoreLoadTd}
                loadRaw={breakdown.cumulativeTD}
                loadCeiling={breakdown.tdCeiling}
                loadPercentile={breakdown.scoreLoadTdPercentile}
                trainingRaw={breakdown.trainingTD}
                matchRaw={breakdown.actualMatchTD}
                runsRaw={breakdown.matchRunsTD}
                matchScore={breakdown.scoreMatchTd}
                matchPerfRaw={breakdown.matchTD}
                matchCeiling={breakdown.matchTdCeiling}
                matchPercentile={breakdown.scoreMatchTdPercentile}
                distinctDays={breakdown.distinctDays}
                ceilingMatchCount={breakdown.ceilingMatchCount}
              />
              <CumulativeSubScoreTile
                label="Cumulative HMLD — 45%"
                overallScore={breakdown.hmldScore}
                qualifyingMatchCount={breakdown.qualifyingMatchCount}
                loadScore={breakdown.scoreLoadHmld}
                loadRaw={breakdown.cumulativeHMLD}
                loadCeiling={breakdown.hmldCeiling}
                loadPercentile={breakdown.scoreLoadHmldPercentile}
                trainingRaw={breakdown.trainingHMLD}
                matchRaw={breakdown.actualMatchHMLD}
                runsRaw={breakdown.matchRunsHMLD}
                matchScore={breakdown.scoreMatchHmld}
                matchPerfRaw={breakdown.matchHMLD}
                matchCeiling={breakdown.matchHmldCeiling}
                matchPercentile={breakdown.scoreMatchHmldPercentile}
                distinctDays={breakdown.distinctDays}
                ceilingMatchCount={breakdown.ceilingMatchCount}
              />
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function Team360Table({ user, onNavigate }) {
  const [period, setPeriod] = useState('week');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('overall');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    setLoading(true);
    getSquadPillarScores(period)
      .then(data => {
        // pg returns NUMERIC columns as strings — normalize once here so both display
        // and sorting work on real numbers.
        const n = v => (v === null || v === undefined ? null : Number(v));
        setRows((data || []).map(r => ({
          ...r,
          physical: n(r.physical), technical: n(r.technical), tactical: n(r.tactical),
          identity: n(r.identity), availability: n(r.availability), overall: n(r.overall),
          stars: n(r.stars),
        })));
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [period]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const toggleExpanded = (playerId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId); else next.add(playerId);
      return next;
    });
  };

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => compareRows(a, b, sortKey, sortDir)),
    [rows, sortKey, sortDir]
  );

  return (
    <PageChrome onNavigate={onNavigate} user={user}>
    <div style={{ maxWidth: 1200 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>360 View — Team Table</h2>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 20px' }}>
        Physical/Technical/Tactical/Identity/Availability from the daily rollup. "no data" means the pillar has nothing to compute from yet — not a score of zero. Expand a row to see how Physical's percentage breaks down.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        {PERIODS.map(p => (
          <button key={p.value} onClick={() => setPeriod(p.value)}
            style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${period === p.value ? '#2563eb' : 'var(--border-strong)'}`, background: period === p.value ? '#2563eb' : 'transparent', color: period === p.value ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {p.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={() => onNavigate('individual-progress')}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            Individual progress
          </button>
        </div>
      </div>

      {loading && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>}

      {!loading && (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border-strong)', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-app)' }}>
                <th style={{ width: 32 }} />
                <SortHeader label="Player" sortKey="name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Position" sortKey="position" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Stars" sortKey="stars" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="left" />
                <SortHeader label="Overall" sortKey="overall" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortHeader label="Physical" sortKey="physical" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortHeader label="Technical" sortKey="technical" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortHeader label="Tactical" sortKey="tactical" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortHeader label="Identity" sortKey="identity" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortHeader label="Availability" sortKey="availability" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(r => {
                const isOpen = expanded.has(r.player_id);
                return (
                  <React.Fragment key={r.player_id}>
                    <tr style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 0 10px 14px' }}>
                        <button onClick={() => toggleExpanded(r.player_id)} title="Show Physical breakdown"
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 2 }}>
                          {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                        </button>
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{r.name}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>{r.position || '—'}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'left' }}>
                        <StarRating value={r.stars} size={13} />
                      </td>
                      <td style={numCell}>
                        {r.overall == null ? (
                          <span style={{ color: 'var(--text-dimmed)' }}>no data</span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end', fontWeight: 700 }}>
                            {r.overall_is_provisional && (
                              <AlertTriangle size={13} color="#f59e0b" title="Provisional — missing Physical data, this is a 4-pillar score not a full 5-pillar one" />
                            )}
                            {Math.round(r.overall)}
                          </span>
                        )}
                      </td>
                      <PillarCell value={r.physical} highlyImputed={r.physical_chronic_highly_imputed} />
                      <PillarCell value={r.technical} />
                      <PillarCell value={r.tactical} />
                      <PillarCell value={r.identity} />
                      <PillarCell value={r.availability} />
                    </tr>
                    {isOpen && <PhysicalBreakdownRow playerId={r.player_id} period={period} />}
                  </React.Fragment>
                );
              })}
              {!sortedRows.length && (
                <tr><td colSpan={COLUMN_COUNT} style={{ padding: 20, textAlign: 'center', color: 'var(--text-dimmed)', fontSize: 13 }}>No active players found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </PageChrome>
  );
}
